import { Dispatch, FC, ReactNode, SetStateAction } from "react";
import {
  Stack,
  Flex,
  Heading,
  Button,
  Box,
  Text,
  HStack,
} from "@chakra-ui/react";
import AccordionComponent from "../../atom/Accordion/AccordionComponent";
import { BULLETIN_DATE, PAGE_SIZE_OPTIONS } from "../../../utils/constants";
import SearchAreaTemplate from "./SearchAreaTemplate";
import { tableStyles } from "../../../utils/styles/tableStyles";
import { TableConfig } from "../../../types/Components/Table/TableConfig";
import { FilterConfig } from "../../../types/Components/Table/FilterConfig";
import AlertDateCountdown from "../../organism/AlertDateCountdown/AlertDateCountdown";
import Pagination from "../../organism/Pagination/Pagination";
import { PaginationConfig } from "../../../types/Components/Table/PaginationConfig";
import { Dayjs } from "dayjs";

interface SearchTableProps {
  handleAddNewItem?: (() => void) | null;
  showModal?: boolean;
  children?: ReactNode;
  alertElement?: ReactNode;
  searchTableName: string;
  summaryName?: string;
  createButtonName?: string;
  accordionTitle?: string;
  headingFontSize?: string;
  tableConfig: TableConfig;
  filterConfig?: FilterConfig;
  paginationConfig: PaginationConfig;
  alertDateCountdownDescription?: string;
  setIsSearching?: Dispatch<SetStateAction<boolean>>;
  setDates?: ((date: Dayjs | null) => void)[];
  headingExtraElement?: ReactNode;
}

const SearchTable: FC<SearchTableProps> = ({
  handleAddNewItem,
  showModal = false,
  children,
  alertElement,
  searchTableName,
  summaryName,
  createButtonName = "Criar item",
  accordionTitle = "Pesquisar e Filtrar",
  headingFontSize,
  tableConfig,
  filterConfig,
  paginationConfig,
  alertDateCountdownDescription = "",
  setIsSearching,
  setDates,
  headingExtraElement,
}) => {
  const {
    tableName = "Lista de Itens",
    dataTable,
    originalDataTable,
    setDataTable,
    searchTitle,
  } = tableConfig;

  const {
    pageSize,
    currentPage,
    totalPages,
    handlePageChange,
    renderContent,
    setPageSize,
    totalItems,
  } = paginationConfig;

  return (
    <Stack spacing={8}>
      <Flex direction="column">
        <Flex direction="row" justifyContent="space-between">
          <Heading fontSize={headingFontSize}>{searchTableName}</Heading>
          {handleAddNewItem && (
            <HStack gap={4}>
              {headingExtraElement}
              <Button variant="primary" onClick={handleAddNewItem}>
                {createButtonName}
              </Button>
            </HStack>
          )}
        </Flex>
        {summaryName && <Text>{summaryName}</Text>}
      </Flex>
      {alertDateCountdownDescription && (
        <AlertDateCountdown
          targetDay={BULLETIN_DATE}
          description={alertDateCountdownDescription}
        />
      )}
      {alertElement}
      {filterConfig && (
        <AccordionComponent accordionTitle={accordionTitle}>
          <Box border="none">
            <SearchAreaTemplate
              setDataTable={setDataTable}
              dataTable={originalDataTable ? originalDataTable : dataTable}
              searchTitle={searchTitle ? searchTitle : ""}
              filterFunction={filterConfig.filterFunction}
              filters={filterConfig.filters}
              keepActiveFilters={filterConfig.keepActiveFilters}
              selectedFilterItem={filterConfig.selectedFilterItem}
              setSelectedFilterItem={filterConfig.setSelectedFilterItem}
              hasInputSearch={filterConfig.hasInputSearch}
              setIsSearching={setIsSearching}
              totalItems={totalItems}
              setDates={setDates}
            >
              {filterConfig.filterChildren}
            </SearchAreaTemplate>
          </Box>
        </AccordionComponent>
      )}
      {showModal && children}

      <Flex direction="column" gap="2.5">
        <Flex
          direction="row"
          gap="4"
          justifyContent="space-between"
          alignItems="center"
        >
          <Heading as="h2" size="md" mb="10px">
            {tableName}
          </Heading>
          {dataTable.length > 0 && (
            <Pagination
              totalResults={totalItems ? totalItems : dataTable.length}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              totalPages={totalPages}
              currentPage={currentPage}
              pageSize={pageSize}
              handlePageChange={handlePageChange}
              setPageSize={setPageSize}
            />
          )}
        </Flex>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          sx={tableStyles}
        >
          {renderContent()}
        </Box>
      </Flex>
    </Stack>
  );
};

export default SearchTable;
